import { castError } from '@sketch/utils'
import { DragEvent, useCallback, useEffect, useState } from 'react'
import { useApolloClient } from '@apollo/client'

import { readShareListItemFromCache } from '../shares/utils'
import { readNestedProjectFromCache } from '../projects'
import { ErrorHandler } from '@sketch/tracing'

import { StorageItemFragment } from '@sketch/gql-types'
import { ClientOrCache } from '@sketch/graphql-cache'
import { DragPayload } from './useDrag'

// Type similar to the src/modules/storageItems/types.ts StorageItemIdentity
type StorageItemIdentity = Pick<
  StorageItemFragment,
  'identifier' | '__typename'
>

const safeguardedParseJson = <T>(content: any) => {
  try {
    return JSON.parse(content) as T
  } catch (error) {
    const errorNormalized = castError(error)
    ErrorHandler.ignore(errorNormalized)

    return
  }
}

const getElementFromCache = (
  cache: ClientOrCache,
  item: StorageItemIdentity
) => {
  if (item.__typename === 'Project') {
    const project = readNestedProjectFromCache(cache, item.identifier)

    if (!project) {
      throw new Error(
        `Project with identifier ${item.identifier} not found in cache`
      )
    }

    return project
  }

  const share = readShareListItemFromCache({ cache, id: item.identifier })

  if (!share) {
    throw new Error(
      `Share with identifier ${item.identifier} not found in cache`
    )
  }

  return share
}

export interface UseDropProps {
  onStorageItemsDragOver?: (
    draggedElement: StorageItemFragment,
    relatableItems: StorageItemFragment[]
  ) => void
  onStorageItemsDropped?: (
    draggedElement: StorageItemFragment,
    relatableItems: StorageItemFragment[]
  ) => void
  onUnknownDragOver?: (event: DragEvent<HTMLElement>) => void
}

/**
 * Hook that exposes attributes to create a droppable area, accepts dragging
 * both shares or projects
 */
export const useDrop = ({
  onStorageItemsDragOver,
  onStorageItemsDropped,
  onUnknownDragOver,
}: UseDropProps) => {
  const [isDraggedOver, setIsDraggedOver] = useState(false)
  const [isDraggedOverItself, setDraggedOverItself] = useState(false)
  const cache = useApolloClient()

  const resetDraggedOver = useCallback(() => {
    setIsDraggedOver(false)
    setDraggedOverItself(false)
  }, [])

  // Validate if the drag has been over to prevent any draggedOver
  // state to prevail incorrectly
  useEffect(() => {
    if (!isDraggedOver) {
      return
    }

    const handleDragEnd = () => {
      resetDraggedOver()
    }

    document.addEventListener('dragend', handleDragEnd)

    return () => {
      document.removeEventListener('dragend', handleDragEnd)
    }
  }, [isDraggedOver, resetDraggedOver])

  const onDragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()

    if (event.dataTransfer.types[0] === 'text/plain') {
      /**
       * The code below runs only until setIsDraggedOver(true) to avoid performance issues during drag.
       * The data-being-dragged attribute, set by useShareDrop, helps track the element; check the hook for details.
       */
      if (!isDraggedOver) {
        if (event.target instanceof HTMLElement) {
          const draggedElement = document.querySelector(
            '[data-being-dragged]'
          ) as HTMLElement

          const isItself = Boolean(draggedElement?.contains(event.target))

          const beingDraggedPayload = safeguardedParseJson<StorageItemIdentity>(
            draggedElement?.dataset.beingDragged
          )
          const draggedRelatedItemsPayload = safeguardedParseJson<
            StorageItemIdentity[]
          >(draggedElement?.dataset.draggedRelatedItems)

          if (!beingDraggedPayload || !draggedRelatedItemsPayload) {
            event.dataTransfer.dropEffect = 'none'
            onUnknownDragOver?.(event)

            return
          }

          event.dataTransfer.dropEffect = 'move'

          const cachedDraggedElement = getElementFromCache(
            cache,
            beingDraggedPayload
          )

          const cacheRelativeDraggedItems = draggedRelatedItemsPayload.map(
            (item: StorageItemIdentity) => getElementFromCache(cache, item)
          )

          onStorageItemsDragOver?.(
            cachedDraggedElement,
            cacheRelativeDraggedItems
          )

          setDraggedOverItself(isItself)
        }

        setIsDraggedOver(true) // This is to prevent the drag over event from being called multiple times
      }
    }
  }

  const onDragLeave = useCallback(
    (event: DragEvent<HTMLElement>) => {
      event.preventDefault()

      /**
       * Check if the element where the cursor moved to and the event.target (where it left from)
       * share the same draggable parent.
       * If true then we should not reset the drag, because it's still inside of the same parent, just moving inside of it.
       *
       * We use event.relatedTarget when available, falling back to document.elementFromPoint
       * for Safari which doesn't reliably populate relatedTarget for drag events.
       *
       * https://linear.app/sketch/issue/SWEB-2895/target-selection-bounds-flickering
       */
      const elementUnderCursor =
        event.relatedTarget ||
        document.elementFromPoint(event.clientX, event.clientY)

      if (
        (elementUnderCursor instanceof Element &&
          elementUnderCursor.closest('[draggable="true"]')) ===
        (event.target instanceof Element &&
          event.target.closest('[draggable="true"]'))
      ) {
        return
      }

      requestAnimationFrame(() => {
        resetDraggedOver()
      })
    },
    [resetDraggedOver]
  )

  if (!onStorageItemsDropped) {
    return {
      kind: 'disabled',
    } as const
  }

  const onDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()

    // Avoid dropping on itself
    if (isDraggedOverItself) return

    resetDraggedOver()

    // If it has a custom drag image, let's make sure it's removed
    document.getElementById('custom-drag-image')?.remove()

    const possibleDragPayload = JSON.parse(
      event.dataTransfer.getData('text/plain') || '{}'
    )

    if (
      !possibleDragPayload ||
      !possibleDragPayload.item ||
      !possibleDragPayload.relatedItems
    ) {
      return
    }

    const dragPayload = possibleDragPayload as DragPayload

    const cachedDraggedElement = getElementFromCache(cache, dragPayload.item)
    const cacheRelativeDraggedItems = dragPayload.relatedItems.map(
      (item: StorageItemIdentity) => getElementFromCache(cache, item)
    )

    onStorageItemsDropped?.(cachedDraggedElement, cacheRelativeDraggedItems)
  }

  return {
    kind: 'enabled',
    isDraggedOver,
    isDraggedOverItself,
    onDrop,
    onDragOver,
    onDragLeave,
  } as const
}
